import { CodeListValue } from "../CodeListValue";
import IndexedDB from "../../../../../../services/IndexedDB/index";
import CodeValuesDB from "../../../../../../services/IndexedDB/CodeValuesDB/index";
import { CodeValueType, CodeValueDisplayType, CodeValueSaveType } from "../../Enums";

export class CodeListUtils {
  /**
   * Get an empty code list value
   */
  private static getEmptyVal = (text: string): CodeListValue => {
    return new CodeListValue(-1, -1, text, "", false, "", new Date(), -1, new Date(), -1, null, null);
  };

  /**
   * @description sort list of code values
   * @param {CodeListValue[]} list - list of code values
   */
  static sortCodeValueList(list: CodeListValue[]) {
    return list.sort((a, b) => {
      if (!a.value) return -1;

      if (a.sortOrder != null && b.sortOrder != null) return CodeListUtils.sortCompare(a.sortOrder, b.sortOrder);

      if (a.sortOrder != null) return -1;

      if (b.sortOrder != null) return 1;

      return CodeListUtils.sortCompare(a.name, b.name);
    });
  }

  private static sortCompare = <T extends number | string>(value1: T, value2: T): number => {
    if (value1 > value2) {
      return 1;
    } else {
      if (value1 < value2) {
        return -1;
      } else {
        return 0;
      }
    }
  };
  static getFormattedCodeValueListByCodeIdAsync = async (
      codeListId: number,
      displayType?,
      saveType?,
      includeEmptyVal: boolean = false,
      emptyText: string = "",
      existingValue?: string | string[]
  ): Promise<CodeListValue[]> => {
    await IndexedDB.initDb();
    let valueList: CodeListValue[] = CodeValuesDB.GetCodeValueListByCodeId(codeListId, existingValue);

    if (valueList) {
      valueList = valueList.map((clVal: CodeListValue) => {
        return {
          ...clVal,
          value: CodeListUtils.getCodeValueSave(clVal, saveType),
          name: CodeListUtils.getCodeValueDisplay(clVal, displayType)
        };
      });

      valueList = CodeListUtils.sortCodeValueList(valueList);

      if (includeEmptyVal) {
        valueList.unshift(CodeListUtils.getEmptyVal(emptyText));
      }

      return valueList;
    } else {
      if (includeEmptyVal) {
        return [CodeListUtils.getEmptyVal(emptyText)];
      } else {
        return [];
      }
    }
  };

  static getFormattedCodeValueListByParentTextAsync = async (
      codeListId: number,
      displayType,
      saveType,
      parentCodeListId: number,
      parentValue: string,
      parentDisplayType: CodeValueDisplayType,
      parentSaveType,
      includeEmptyVal: boolean = false,
      emptyText: string = "",
      existingValue?: string | string[]
  ): Promise<CodeListValue[]> => {
    await IndexedDB.initDb();
    const parentCodeValues: CodeListValue[] = await CodeListUtils.getFormattedCodeValueListByCodeIdAsync(parentCodeListId, parentDisplayType, parentSaveType);
    let valueList: CodeListValue[] = [];
    const parentCLValue: CodeListValue = parentCodeValues.find(
      cl => (cl.value || "").toLowerCase() == (parentValue || "").toLowerCase()
    );

    if (parentCLValue) {
      valueList = await CodeListUtils.getFormattedCodeValueListByCodeIdAsync(codeListId, displayType, saveType, includeEmptyVal, emptyText, existingValue);
      valueList = valueList.filter(clVal => clVal.parentCodeListValueId == parentCLValue.id);
    }

    return CodeListUtils.sortCodeValueList(valueList);
  };

  public static getCodeValueSave = (code: CodeListValue, saveType: CodeValueSaveType): string => {
    //empty value should always show empty value text
    if (code.value === "") {
      return code.name;
    }

    switch (saveType) {
      case CodeValueSaveType.DescriptionOnly:
        return code.name;
      case CodeValueSaveType.ValueOnly:
        return code.value;
      case CodeValueSaveType.ValueAndDescription:
        return code.value.concat(" - ", code.name);
      case CodeValueSaveType.DescriptionAndValue:
        return code.name.concat(" - ", code.value);
      default:
        return code.name;
    }
  };

  public static getCodeValueDisplay = (code: CodeListValue, displayType: CodeValueDisplayType): string => {
    //empty value should always show empty value text
    if (code.value === "") {
      return code.name;
    }

    switch (displayType) {
      case CodeValueDisplayType.DescriptionOnly:
        return code.name;
      case CodeValueDisplayType.ValueOnly:
        return code.value;
      case CodeValueDisplayType.ValueAndDescription:
        return code.value.concat(" - ", code.name);
      case CodeValueDisplayType.DescriptionAndValue:
        return code.name.concat(" - ", code.value);
      default:
        return code.name;
    }
  };
}
